import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    ViewChild,
} from "@angular/core";
import { filter, map, Observable, OperatorFunction } from "rxjs";
import { v4 } from "uuid";

import exportedSass from "@hermes/aphrodite/design-system-tools";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { generateCountryPath } from "@hermes/shop-utils";
import {
    CityEntry,
    CountryEntry,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_COUNTRY_MODE,
    SHOP_LOCATIONS_DEFAULT_MODE,
    SHOP_LOCATIONS_NO_SHOPS_MODE,
    SHOP_LOCATIONS_UNINITIALIZED_MODE,
    ShopLocationsFacade,
    ShopLocationsPageMode,
} from "@hermes/states/shop-locations";
import { TrayFacade } from "@hermes/states/tray";
import { RouterService } from "@hermes/utils-generic/services/router";

import { ShopLocatorItemMode } from "../shop-locator-navigation-item/shop-locator-navigation-item.component";

/**
 * Definition for the City item of its state according to the mode of the ShopLocator page
 */
const CITY_ITEM_VISIBILITY: Record<ShopLocationsPageMode, ShopLocatorItemMode> =
    {
        [SHOP_LOCATIONS_UNINITIALIZED_MODE]: "none",
        [SHOP_LOCATIONS_NO_SHOPS_MODE]: "none",
        [SHOP_LOCATIONS_DEFAULT_MODE]: "none",
        [SHOP_LOCATIONS_COUNTRY_MODE]: "button",
        [SHOP_LOCATIONS_CITY_MODE]: "selected",
    };

/**
 * Type describing the type of Item that submitted the event.
 */

const CITY = $localize`:@@hermes_global.city:City`;
const COUNTRY = $localize`:@@hermes.store_locator.navigation_bar.country:Country`;
const LOCATION = $localize`:@@hermes-country-selector-tray.country:Location`;

@Component({
    selector: "h-shop-locator-navigation",
    templateUrl: "./shop-locator-navigation.component.html",
    styleUrls: ["./shop-locator-navigation.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ShopLocatorNavigationComponent {
    @ViewChild("country", {
        read: ElementRef,
    })
    public countryButton!: ElementRef<HTMLElement>;

    @ViewChild("city", {
        read: ElementRef,
    })
    public cityButton?: ElementRef<HTMLElement>;

    public isModalOpen = false;

    public currentTranslatedCountry$ =
        this.shopLocationFacade.currentCountry$.pipe(
            map(
                (country: CountryEntry | undefined) =>
                    country?.translatedCountry ?? COUNTRY,
            ),
        );

    public countryTitle$ = this.currentTranslatedCountry$.pipe(
        map((country: string) => `${country} - ${LOCATION}`),
    );

    public currentTranslatedCity$ = this.shopLocationFacade.currentCity$.pipe(
        map((city: CityEntry | undefined) => city?.translatedCity ?? CITY),
    );

    public cityTitle$ = this.currentTranslatedCity$.pipe(
        map((city: string) => `${city} - ${LOCATION}`),
    );

    public cityItemMode$: Observable<ShopLocatorItemMode> =
        this.shopLocationFacade.mode$.pipe(
            map((mode) => CITY_ITEM_VISIBILITY[mode]),
        );

    public countryHref$: Observable<string> =
        this.shopLocationFacade.currentCountry$.pipe(
            filter((country) => !!country) as OperatorFunction<
                CountryEntry | undefined,
                CountryEntry
            >,
            map((country) => generateCountryPath(country.slugifiedCountry)),
        );

    constructor(
        public shopLocationFacade: ShopLocationsFacade,
        private trayFacade: TrayFacade,
        private routerService: RouterService,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public openTray(mode: "countries" | "cities", event: Event): void {
        event.preventDefault();
        if (this.isModalOpen) {
            return;
        }
        this.isModalOpen = true;
        this.trayFacade.openTray({
            hasOverlay: true,
            hasCategoryInstitutional: false,
            isOpen: true,
            position: "right",
            titleKey: "hermes-country-selector-tray.country",
            uuid: v4(),
            name: `shop-locations-${mode}-tray`,
            metadata: {},
            backgroundColor: exportedSass.miscellanousColors["color-white"],
            closeCallback: () => this.setFocusWithModalState(mode),
        });
    }

    public navigateToFindStore(): void {
        this.routerService.spaRedirect(`${this.locale.urlPrefix}/find-store/`);
    }

    private setFocusWithModalState = (mode: "countries" | "cities"): void => {
        this.isModalOpen = false;

        return mode === "countries"
            ? this.countryButton.nativeElement.focus()
            : this.cityButton?.nativeElement.focus();
    };
}
